import crypto from 'crypto';
import {
  stringifyQueryParam,
  getOriginForConfig,
  getPathnameForConfig as getPathnameForFlow,
  WidgetFlow,
} from '@core-sdk';
import { getEnvironmentFromApiKey } from '@common';
import { MoonPayURLAuth, MoonPayWidgetQueryParams } from '../../types';
import { GenerateSignatureOptions } from './types';
import { Logger } from '../../logger';
import { UrlGenerationFailed, UrlParsingFailed } from './errors';

/**
 * Class for methods relating to MoonPay URLs.
 */
export class MoonPayURL {
  /**
   * @param secretKey The secret key for the client
   */
  constructor(private secretKey: string, private logger: Logger) {}

  /**
   * Generate and return a signature for a given URL.
   * @param url The URL for which to generate the signature.
   * @returns The generated signature.
   */
  generateSignature(
    url: string,
    { returnFullUrl: returnFullURL }: GenerateSignatureOptions = {
      returnFullUrl: false,
    },
  ): string | null {
    const constructedURL = this.constructUrl(url);
    if (!constructedURL) {
      return null;
    }

    const signature = this.constructSignature(constructedURL);
    if (!returnFullURL) {
      return signature;
    }

    constructedURL.searchParams.set('signature', signature);
    return constructedURL.toString();
  }

  /**
   * Verify that a signed URL is valid.
   * @param url The URL to verify.
   * @returns `true` if the URL is valid, otherwise `false`.
   */
  public isSignatureValid(urlString: string): boolean {
    let url: URL;

    try {
      url = new URL(urlString);
    } catch {
      return false;
    }

    const apiKeyParam = url.searchParams.get('apiKey');

    if (!apiKeyParam) {
      return false;
    }

    const signatureParam = url.searchParams.get('signature');

    if (!signatureParam) {
      return false;
    }

    url.searchParams.delete('signature');

    const signature = this.constructSignature(url);

    try {
      return crypto.timingSafeEqual(
        Buffer.from(signatureParam, 'base64'),
        Buffer.from(signature, 'base64'),
      );
    } catch {
      return false;
    }
  }

  /**
   * Generates and returns a signed URL for a given flow and set of parameters.
   *
   * @template TFlow - A generic type that extends WidgetFlow.
   *
   * @param {Object} config - The config object.
   * @param {TFlow} config.flow - The flow type.
   * @param {MoonPayWidgetQueryParams<TFlow>} config.params - The query parameters for the widget.
   * @param {MoonPayURLAuth} [config.auth] - Optional authentication for the URL.
   *
   * @throws {Error} Throws an error if the config is invalid.
   *
   * @returns {string} Returns the constructed URL as a string or null if the URL cannot be constructed.
   *
   * @example
   * const url = moonPay.url.generate({
   *   flow: 'buy',
   *   params: { apiKey: 'pk_test', ... },
   * });
   */
  generate<TFlow extends WidgetFlow>({
    flow,
    params,
    auth,
  }: {
    flow: TFlow;
    params: MoonPayWidgetQueryParams<TFlow>;
    auth?: MoonPayURLAuth;
  }): string {
    if ('url' in params) {
      const url = this.constructUrl(params.url);

      url.searchParams.delete('signature');

      const signature = this.constructSignature(url);
      url.searchParams.set('signature', signature);
      return url.toString();
    }

    const environment = getEnvironmentFromApiKey(this.secretKey);

    const origin = getOriginForConfig({
      environment,
      flow,
    });

    const pathname = getPathnameForFlow({ flow });

    if (!pathname || !origin) {
      const error = new UrlGenerationFailed();
      this.logger.error(error.message);
      throw error;
    }

    const url = this.constructUrl(pathname, origin);

    Object.entries(params).forEach(([key, value]) => {
      const stringifiedValue = stringifyQueryParam(value);

      if (!stringifiedValue) {
        return;
      }

      url.searchParams.append(key, stringifiedValue);
    });

    const signature = this.constructSignature(url);
    url.searchParams.append('signature', signature);

    if (auth) {
      const oneTimeTokenUrl = this.constructUrl(
        `/oauth/app/${auth.token}`,
        url.origin,
      );

      if (!oneTimeTokenUrl) {
        const error = new UrlGenerationFailed();
        this.logger.error(error.message);
        throw error;
      }

      oneTimeTokenUrl.search = url.search;

      oneTimeTokenUrl.searchParams.append(
        'redirectTo',
        `${url.pathname}${url.search}`,
      );
      return oneTimeTokenUrl.toString();
    }

    return url.toString();
  }

  private constructUrl(path: string, base?: string): URL {
    try {
      const constructedUrl = new URL(path, base);
      return constructedUrl;
    } catch (e) {
      const error = new UrlParsingFailed();
      this.logger.error(error.message);
      throw error;
    }
  }

  private constructSignature(url: URL): string {
    return crypto
      .createHmac('sha256', this.secretKey)
      .update(url.search)
      .digest('base64');
  }
}
